import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge, Button, ControlRow, FormField, Input, Modal, ModalBody, ModalFooter, ModalHeader,
  Select, Switch, useToast,
} from '@vd/ui';
import { TICKET_TYPE_GLYPHS, ticketTypeGlyphLabel } from '@vd/annotation-core';
import { Trash2 } from 'lucide-react';
import type { TicketType } from '@vd/types';
import { api } from '../../../lib/api';
import { useAuth, useCurrentCompany } from '../../../features/auth/AuthProvider';
import { MarkerButton } from '../../../features/ticketTypes/MarkerButton';
import { MarkerPicker } from '../../../features/ticketTypes/MarkerPicker';
import { TypeMarker } from '../../../features/ticketTypes/TypeMarker';
import styles from '../Settings.module.css';

/**
 * What kinds of thing this workspace tracks (TOADO-410).
 *
 * The company set. A project's differences from it live on the project's own
 * settings page, because that is where somebody is when they want them.
 *
 * Markers are picked from a fixed set (`TICKET_TYPE_GLYPHS`, shared with the
 * renderer) rather than typed, and that is the constraint that makes an editable
 * type set drawable at all: a workspace can call a type anything, but its marker
 * has to be something an installed extension build can render. A free text field
 * would produce types that silently all look the same on the page, because a build
 * that has never heard of a glyph draws the generic marker (TOADO-297).
 */

export function TicketTypesTab() {
  const { currentCompanyId } = useAuth();
  const isOwner = useCurrentCompany()?.role === 'owner';
  const qc = useQueryClient();
  const toast = useToast();

  const [name, setName] = useState('');
  const [glyph, setGlyph] = useState(TICKET_TYPE_GLYPHS[0]!.value);
  const [defaultForNewProjects, setDefaultForNewProjects] = useState(true);
  const [deleting, setDeleting] = useState<TicketType | null>(null);

  const types = useQuery({
    queryKey: ['ticketTypes', 'company', currentCompanyId],
    queryFn: () => currentCompanyId
      ? api.listCompanyTicketTypes(currentCompanyId).then((r) => r.items)
      : Promise.resolve([]),
    enabled: !!currentCompanyId,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['ticketTypes'] });
    // Every board's cards carry a type name, so a rename has to reach them too.
    void qc.invalidateQueries({ queryKey: ['tickets'] });
  };

  const create = useMutation({
    mutationFn: () => api.createTicketType(currentCompanyId!, { name: name.trim(), glyph, defaultForNewProjects }),
    onSuccess: () => {
      setName('');
      invalidate();
      toast.push({ variant: 'success', title: 'Ticket type added.' });
    },
    onError: (error: unknown) => toast.push({
      variant: 'danger',
      title: 'Could not add that ticket type.',
      description: error instanceof Error ? error.message : undefined,
    }),
  });

  const update = useMutation({
    mutationFn: ({ typeId, body }: { typeId: string; body: Parameters<typeof api.updateTicketType>[2] }) =>
      api.updateTicketType(currentCompanyId!, typeId, body),
    onSuccess: invalidate,
    onError: (error: unknown) => toast.push({
      variant: 'danger',
      title: 'Could not save that change.',
      description: error instanceof Error ? error.message : undefined,
    }),
  });

  const sorted = useMemo(
    () => [...(types.data ?? [])].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name)),
    [types.data],
  );

  /**
   * A non-owner gets a plain list, not a disabled editor.
   *
   * A form that will not take a caret reads as a page that is failing rather than
   * one that is finished - Jason reported exactly that about the committed
   * walkthrough review page, and the fix there was to render a record that says
   * what happened instead (commit 7b935e54).
   */
  if (!isOwner) {
    return (
      <>
        <h1 className={styles.h1}>Ticket types</h1>
        <p className={styles.sub}>
          The kinds of thing this workspace tracks. Only an owner can change them.
        </p>
        <section className={styles.section}>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
            {sorted.map((type) => (
              <li key={type.id}>{type.name} <span style={{ color: 'var(--text-tertiary)' }}>({ticketTypeGlyphLabel(type.glyph)})</span></li>
            ))}
            {sorted.length === 0 && <li style={{ color: 'var(--text-tertiary)' }}>None yet.</li>}
          </ul>
        </section>
      </>
    );
  }

  return (
    <>
      <h1 className={styles.h1}>Ticket types</h1>
      <p className={styles.sub}>
        Bug, improvement, new feature, and whatever else you track. A ticket&apos;s type shows on its
        card, filters the board, and tells an agent what the work is. A project can rename or hide
        the types you add in its own settings.
      </p>
      <p className={styles.sub}>
        Bug, Improvement and New feature are built in. Any project can turn them off, but they
        can&apos;t be renamed, given another marker or deleted.
      </p>

      <section className={styles.section}>
        {sorted.map((type) => (
          <div
            key={type.id}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) 170px auto auto',
              gap: 8,
              alignItems: 'center',
              paddingBlock: 6,
            }}
          >
            {/* A built-in type keeps its name and marker (TOADO-578); the API
                refuses a change to either, so the controls are not offered. */}
            {type.builtin ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{type.name}</span>
                <Badge variant="outline">Built in</Badge>
              </span>
            ) : (
            <Input
              defaultValue={type.name}
              aria-label={`Name for ${type.name}`}
              // Saved on blur rather than per keystroke: the unique-name check is
              // server-side and a rename in progress is briefly a name somebody
              // else already has.
              onBlur={(e) => {
                const next = e.target.value.trim();
                if (next && next !== type.name) update.mutate({ typeId: type.id, body: { name: next } });
              }}
            />
            )}
            {type.builtin ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
                <TypeMarker glyph={type.glyph} height={22} /> {ticketTypeGlyphLabel(type.glyph)} marker
              </span>
            ) : (
            // A marker this build does not list stays in the picker, so opening it
            // cannot silently change one somebody chose on a newer build.
            <MarkerButton
              glyph={type.glyph}
              typeName={type.name}
              onChange={(next) => update.mutate({ typeId: type.id, body: { glyph: next } })}
            />
            )}
            <ControlRow>
              <Switch
                checked={type.defaultForNewProjects}
                onChange={(e) => update.mutate({
                  typeId: type.id,
                  body: { defaultForNewProjects: e.target.checked },
                })}
                aria-label={`Offer ${type.name} on new projects`}
              />
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>New projects</span>
            </ControlRow>
            {type.builtin ? (
              // Holds the column so the rows stay aligned; says why there is no Delete.
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', paddingInline: 8 }}>
                Can&apos;t be deleted
              </span>
            ) : (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Trash2 size={14} />}
              onClick={() => setDeleting(type)}
              aria-label={`Delete ${type.name}`}
            >
              Delete
            </Button>
            )}
          </div>
        ))}
        {sorted.length === 0 && !types.isLoading && (
          <p style={{ color: 'var(--text-tertiary)' }}>No ticket types yet. Add the first one below.</p>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Add a type</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'end' }}>
          <FormField label="Name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tech debt"
              maxLength={40}
            />
          </FormField>
          <Button
            variant="primary"
            disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Adding…' : 'Add'}
          </Button>
        </div>
        <div style={{ height: 12 }} />
        <MarkerPicker value={glyph} onChange={setGlyph} />
        <div style={{ height: 12 }} />
        <ControlRow>
          <Switch
            checked={defaultForNewProjects}
            onChange={(e) => setDefaultForNewProjects(e.target.checked)}
          />
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            Offer this on new projects
          </span>
        </ControlRow>
      </section>

      {deleting && (
        <DeleteTypeModal
          type={deleting}
          others={sorted.filter((type) => type.id !== deleting.id)}
          companyId={currentCompanyId!}
          onClose={() => setDeleting(null)}
          onDone={(message) => {
            setDeleting(null);
            invalidate();
            toast.push({ variant: 'success', title: message });
          }}
        />
      )}
    </>
  );
}

/**
 * Deleting a type, with the count shown before the choice.
 *
 * The count comes first because "this will untype 340 tickets" is not something to
 * discover afterwards. The server refuses the delete outright unless it is told
 * what to do with them - the foreign key is ON DELETE RESTRICT for exactly this
 * reason - so there is no path where the choice is made by omission.
 */
export function DeleteTypeModal({ type, others, companyId, onClose, onDone }: {
  type: TicketType;
  others: TicketType[];
  companyId: string;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [reassignTo, setReassignTo] = useState<string>('');
  const usage = useQuery({
    queryKey: ['ticketTypeUsage', companyId, type.id],
    queryFn: () => api.ticketTypeUsage(companyId, type.id),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteTicketType(companyId, type.id, reassignTo
      ? { reassignTo }
      : { clear: true }),
    onSuccess: (result) => onDone(
      result.ticketCount === 0
        ? `Deleted ${type.name}.`
        : reassignTo
          ? `Deleted ${type.name} and moved ${result.ticketCount} ticket${result.ticketCount === 1 ? '' : 's'}.`
          : `Deleted ${type.name} and untyped ${result.ticketCount} ticket${result.ticketCount === 1 ? '' : 's'}.`,
    ),
  });
  const count = usage.data?.ticketCount ?? 0;

  return (
    <Modal open onClose={onClose} width={460}>
      <ModalHeader>Delete {type.name}</ModalHeader>
      <ModalBody>
        {usage.isLoading && <p>Checking how many tickets use this…</p>}
        {/* Without the count, Delete would untype them unasked: it waits for one. */}
        {usage.isError && (
          <p style={{ color: 'var(--danger)' }}>
            Toado could not check how many tickets use this type, so it cannot be deleted yet. Try again in a moment.
          </p>
        )}
        {usage.isSuccess && count === 0 && (
          <p>No tickets use this type. Deleting it changes nothing else.</p>
        )}
        {usage.isSuccess && count > 0 && (
          <>
            <p>
              <strong>{count} ticket{count === 1 ? '' : 's'}</strong> use this type.
              Deleting it does not delete them.
            </p>
            <FormField label="What happens to them">
              <Select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}>
                <option value="">Leave them with no type</option>
                {others.map((other) => (
                  <option key={other.id} value={other.id}>Move them to {other.name}</option>
                ))}
              </Select>
            </FormField>
          </>
        )}
        {remove.isError && (
          <p style={{ color: 'var(--danger)' }}>
            {remove.error instanceof Error ? remove.error.message : 'Could not delete that type.'}
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button
          variant="destructive"
          disabled={!usage.isSuccess || remove.isPending}
          onClick={() => remove.mutate()}
        >
          {remove.isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
